<template>
    <div class="text-editor">
        <div
            class="toggle-raw"
            v-if="
                !props.disableRaw &&
                model.mimetype?.includes('text/markdown') &&
                !props.readingMode
            "
        >
            <label for="rawMode">Raw&nbsp;</label>
            <ToggleSwitch v-model="rawMode" inputId="rawMode" />
        </div>
        <div ref="type" class="text-editor-wrapper flex-fill">
            <c-text-editor
                v-if="(!rawMode || props.readingMode) && domain"
                ref="editor"
                :data-editor-extensions="
                    [
                        'text-type-menu',
                        'strikethrough',
                        'link-edit-box',
                        'inline-insert-menu',
                        'mystekz-business-entities',
                        'mystekz-roles',
                        'mystekz-domains',
                        'mystekz-products',
                        'mystekz-processes',
                        'code-languages',
                        'mermaid',
                        'link-preview',
                    ].join(',')
                "
                @c-text-editor:register="registerEditor"
                @c-text-editor:changed="documentChanged"
                class="markdown-editor"
                :readingMode="props.readingMode"
            ></c-text-editor>
            <div
                v-else-if="rawMode || !model.mimetype.includes('text/markdown')"
                ref="codeMirrorEditor"
            ></div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import "@/wc/text-editor/component/text-editor";
import { defaultKeymap } from "@codemirror/commands";
import { StreamLanguage } from "@codemirror/language";
import { gherkin } from "@codemirror/legacy-modes/mode/gherkin";
import { EditorState as CodeMirrorState } from "@codemirror/state";
import { EditorView as CodeMirrorView, keymap } from "@codemirror/view";
import { basicSetup } from "codemirror";
import { ref, onUnmounted, watch, nextTick } from "vue";
import { Artefact } from "@/lib/artefacts";
import { useCurrentEntityManager } from "@/stores/entity-store";

const model = defineModel<Artefact>({ required: true });
const props = withDefaults(
    defineProps<{
        disableRaw?: boolean;
        readingMode?: boolean;
    }>(),
    {
        disableRaw: false,
        readingMode: false,
    }
);

const { domain } = useCurrentEntityManager();
const editorHooks = ref();

const registerEditor = (event: CustomEvent) => {
    editorHooks.value = event.detail.value;
    if (model.value.content) {
        editorHooks.value.load(model.value.content);
    }
};

// Sometimes the artefact is changed from the outside (e.g. when the user
// discards their changes).
watch(
    () => model.value.content,
    (newContent) => {
        const currentMarkdown = editorHooks.value?.getMarkdown?.();
        if (editorHooks.value && newContent !== currentMarkdown) {
            editorHooks.value.load(newContent);
        }
    },
    { immediate: true }
);

const documentModified = ref(false);
const documentChanged = () => {
    // Make sure to update the value asap, but in a separate thread. Not sure
    // why this is needed, it appears the event is triggered before the value
    // can be returned through the hooks.
    setTimeout(() => {
        if (!documentModified.value) {
            const newMarkdown = editorHooks.value?.getMarkdown?.();
            if (newMarkdown !== model.value.content) {
                documentModified.value = true;
            }
        }
        if (documentModified.value) {
            model.value.content = editorHooks.value?.getMarkdown?.();
        }
    }, 0);
};

const editor = ref<CodeMirrorView>();
onUnmounted(() => {
    editor.value?.destroy?.();
});

const rawMode = ref(false);
const codeMirrorEditor = ref<HTMLDivElement>();

const initProseMirror = () => {
    if (rawMode || !model.value.mimetype.includes("text/markdown")) {
        const trackChanges = CodeMirrorState.transactionExtender.of((tr) => {
            if (tr.docChanged) {
                model.value.content = tr.newDoc.toString();
            }
            return {};
        });
        const extensions = [
            basicSetup,
            trackChanges,
            keymap.of(defaultKeymap),
            CodeMirrorView.contentAttributes.of({ spellcheck: "true" }),
            CodeMirrorView.lineWrapping,
            StreamLanguage.define(gherkin),
        ];
        editor.value = new CodeMirrorView({
            state: CodeMirrorState.create({
                doc: model.value.content,
                extensions,
            }),
            parent: codeMirrorEditor.value,
        });
    }
};

watch(
    rawMode,
    (toRawMode) => {
        if (toRawMode) {
            nextTick(() => {
                initProseMirror();
            });
        }
    },
    { immediate: true }
);
</script>

<style lang="scss" scoped>
// Import is somewhat deprecated, but use is not yet supported here...
// Shared markdown styling between viewer and editor
@import "@/shared/styles/markdown";

.flex-fill {
    display: flex;
    flex-direction: column;

    > * {
        flex: 1;
    }
}

.text-editor {
    .toggle-raw {
        position: absolute;
        top: 1rem;
        left: 50%;
        transform: translateX(-50%);

        label {
            font-weight: normal;
            display: inline-block;
            vertical-align: middle;
            margin-top: -1rem;
        }

        .p-toggleswitch {
            //transform: scale(0.8)
            :deep(.p-toggleswitch-slider) {
                box-shadow: none;
            }

            &.p-toggleswitch-checked :deep(.p-toggleswitch-slider) {
                background: #707070;

                &:hover {
                    background: #808080;
                }
            }
        }
    }
}
</style>
<style lang="scss">
// We can style nodes in the document using css parts
// Current styling is only a subset of the old markdown styling
c-text-editor::part(node-heading-1),
c-text-editor::part(node-heading-2) {
    border-bottom: 1px solid var(--stekz-outline);
}

c-text-editor::part(node-blockquote) {
    // Vertical padding to make the border include the child's margins
    padding: 0.01rem 1rem;
    border-left: 4px solid var(--stekz-primary-color);
    margin: 0;
}
</style>
